import React, { useState } from 'react';
import { businessAPI } from '../../api/businesses';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const isTempEmail = (email) => {
  return email && email.endsWith('.temp');
};

const AddStaffModal = ({ businessId, onClose, onSuccess, staffMember }) => {
  const [loading, setLoading] = useState(false);
  const [staffImageFile, setStaffImageFile] = useState(null);
  const [staffImagePreview, setStaffImagePreview] = useState('');
  const isEdit = !!staffMember;
  const hasTempEmail = isEdit && isTempEmail(staffMember?.email);
  
  const [formData, setFormData] = useState({
    email: hasTempEmail ? '' : (staffMember?.email || ''),
    first_name: staffMember?.first_name || '',
    last_name: staffMember?.last_name || '',
    phone: staffMember?.phone || '',
    position: staffMember?.position || 'Staff'
  });

  React.useEffect(() => {
    if (staffMember) {
      const staffHasTempEmail = isTempEmail(staffMember.email);
      setFormData({
        email: staffHasTempEmail ? '' : (staffMember.email || ''),
        first_name: staffMember.first_name || '',
        last_name: staffMember.last_name || '',
        phone: staffMember.phone || '',
        position: staffMember.position || 'Staff'
      });
      if (staffMember.profile_image) {
        setStaffImagePreview(staffMember.profile_image);
      }
    }
  }, [staffMember]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...formData,
        staff_image: staffImageFile || undefined
      };
      
      // Don't send email if it's empty and we had a temp email
      if (isEdit && hasTempEmail && !data.email) {
        delete data.email;
      }

      if (isEdit) {
        await businessAPI.updateStaff(businessId, staffMember.id, data);
      } else {
        await businessAPI.addStaff(businessId, data);
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save staff:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">{isEdit ? 'Edit Staff Member' : 'Add Staff Member'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div>
            <label className="block text-sm font-medium mb-1">First Name *</label>
            <input
              type="text"
              required
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              className="w-full input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Last Name *</label>
            <input
              type="text"
              required
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              className="w-full input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full input"
              placeholder="staff@example.com (optional)"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <PhoneInput
              country={'pk'}
              value={formData.phone}
              onChange={(phone) =>
                setFormData({
                  ...formData,
                  phone: phone ? (phone.startsWith('+') ? phone : `+${phone}`) : ''
                })
              }
              inputClass="!w-full !h-10 !text-sm"
              containerClass="mt-1"
              enableSearch
              countryCodeEditable={false}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Position</label>
            <input
              type="text"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              className="w-full input"
              placeholder="e.g., Senior Stylist, Manager"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Staff Image (Optional)</label>
            {staffImagePreview ? (
              <img
                src={staffImagePreview}
                alt="Staff preview"
                className="h-20 w-20 rounded-full object-cover border mb-3"
              />
            ) : null}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) {
                  setStaffImageFile(null);
                  setStaffImagePreview('');
                  return;
                }
                setStaffImageFile(file);
                try {
                  const url = URL.createObjectURL(file);
                  setStaffImagePreview(url);
                } catch (err) {
                  setStaffImagePreview('');
                }
              }}
              className="w-full input"
            />
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update Staff' : 'Add Staff')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStaffModal;
